import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme, { mobileBreakpoint } from '../theme';
import config from '../config';
import PageLayout from './PageLayout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import LessonBookingPopup from './LessonBookingPopup';
import { MessageStrip, MessageIcon, MessageText } from './shared/CommonElements';

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
  margin-top: -5px;
`;

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`;



const MainTitle = styled.h1`
  font-size: 64px;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto 48px auto;
  padding: 40px 24px 48px 24px;
  background: linear-gradient(135deg, #fff 100%,#fff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.primary};
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const Dropdown = styled.select`
  padding: 12px 16px;
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  background-color: white;
  color: ${theme.colors.primary};
  font-weight: 500;
  margin-bottom: 10px;
`;

const LessonList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 16px;
  color: ${theme.colors.secondary};
`;

const LessonItem = styled.li`
  margin-bottom: 6px;
  font-weight: 500;
`;

const ContractNotice = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 18px 20px;
  color: #333;
  font-size: 15px;
  margin-top: 10px;
  text-align: center;
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 14px;
  }
`;

const AGBLink = styled.a`
  color: ${theme.colors.secondary};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
`;

const ActionButton = styled.button`
  margin: 32px auto 0 auto;
  min-width: 300px;
  display: block;
  margin-bottom: -20px;
  padding: 14px 32px;
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #e8316d 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover:enabled {
    background: linear-gradient(135deg, #e8316d 0%, ${theme.colors.secondary} 100%);
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin: 32px 0 0 0;
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CancelButton = styled.button`
  margin: 32px auto 0 auto;
  min-width: 300px;
  display: block;
  margin-bottom: -20px;
  padding: 14px 32px;
  background: #eee;
  color: ${theme.colors.primary};
  border: 0px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: #f5f5f5;
    color: ${theme.colors.secondary};
    transform: scale(1.01);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;

const PriceSection = styled.div`
  margin-bottom: 10px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.secondary};
`;

const PricePerLessonSection = styled.div`
  margin-top: -10px;
  margin-bottom: 10px;
  text-align: center;
  font-size: 1em;
  font-weight: 700;
  color: ${theme.colors.secondary};
`;

const sampleCategories = [
  { key: 'CHILDREN', label: 'Kinder', description: 'Für Kinder' },
  { key: 'ADULTS', label: 'Erwachsene', description: 'Für Erwachsene' },
  { key: 'SPECIAL', label: 'Spezial', description: 'Spezialkurse' },
];


const SegmentedButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 10px;
  width: 100%;
`;

const SegmentedButton = styled.button`
  z-index: 1000;
  flex: 1 1 0;
  padding: 10px 22px;
  border: 2px solid ${theme.colors.accent1};
  background: ${({ active, disabled }) => 
    disabled ? '#f5f5f5' : 
    active ? theme.colors.secondary : '#fff'};
  color: ${({ active, disabled }) => 
    disabled ? '#999' : 
    active ? '#fff' : theme.colors.primary};
  font-weight: 600;
  border-radius: 24px;
  font-size: 16px;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  outline: none;
  box-shadow: ${({ active }) => active ? '0 2px 8px rgba(219, 39, 98, 0.08)' : 'none'};
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};
  &:hover {
    background: ${({ active, disabled }) => 
      disabled ? '#f5f5f5' :
      active ? theme.colors.secondary : theme.colors.accent1};
    color: ${({ active, disabled }) => 
      disabled ? '#999' :
      active ? '#fff' : theme.colors.primary};
  }
`;

const descriptionBoxBg = {
  CHILDREN: 'rgba(188, 231, 253, 0.13)',      // light blue
  ADULTS: 'rgba(196, 146, 177, 0.08)',     // light purple
  SPECIAL: 'rgba(219, 39, 98, 0.08)',     // light pink/red
};

const DescriptionBox = styled.div`
  background: ${({ category }) => descriptionBoxBg[category] || 'white'};
  border-radius: ${theme.borderRadius.medium};
  border-left: 1px solid ${({ category }) =>
    category === 'CHILDREN' ? theme.colors.accent2 :
    category === 'ADULTS' ? theme.colors.accent3 :
    theme.colors.secondary};
  border-right: 1px solid ${({ category }) =>
    category === 'CHILDREN' ? theme.colors.accent2 :
    category === 'ADULTS' ? theme.colors.accent3 :
    theme.colors.secondary};
  border-top: 1px solid ${({ category }) =>
    category === 'CHILDREN' ? theme.colors.accent2 :
    category === 'ADULTS' ? theme.colors.accent3 :
    theme.colors.secondary};
  border-bottom: 1px solid ${({ category }) =>
    category === 'CHILDREN' ? theme.colors.accent2 :
    category === 'ADULTS' ? theme.colors.accent3 :
    theme.colors.secondary};
  padding: 28px 18px;
  color: ${theme.colors.primary};
  font-size: 1.1rem;
  margin-top: 10px;
  text-align: center;
  font-weight: 500;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 18px 0 0 0;
  font-size: 1rem;
`;

const Checkbox = styled.input`
  margin-top: 3px;
  accent-color: ${theme.colors.secondary};
`;

const WiderrufLink = styled.a`
  color: ${theme.colors.secondary};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
`;

const ComingSoonText = styled.span`
  font-size: 12px;
  color: #999;
  font-style: italic;
  margin-left: 8px;
`;

const LessonPackagePurchasePage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState(sampleCategories[0].key);
  const [lessonPackages, setLessonPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [widerrufChecked, setWiderrufChecked] = useState(false);
  const [agbChecked, setAgbChecked] = useState(false);
  const [earlyExecutionChecked, setEarlyExecutionChecked] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [showSlotsPopup, setShowSlotsPopup] = useState(false);
  
  // Billing data state
  const [billingData, setBillingData] = useState({
    name: '-',
    street: '-',
    zipCode: '-',
    city: '-'
  });
  const [billingDataLoading, setBillingDataLoading] = useState(false);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < mobileBreakpoint);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!token) {
      setError('Bitte logge dich ein, um Stundenpakete zu laden.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    fetch(config.getApiUrl('/api/mypage/lesson-packages'), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Fehler beim Laden der Stundenpakete');
        return res.json();
      })
      .then(data => {
        setLessonPackages(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  // Fetch billing data on component mount
  useEffect(() => {
    const fetchBillingData = async () => {
      if (!token) return;
      
      setBillingDataLoading(true);
      
      try {
        const res = await fetch(config.getApiUrl('/api/mypage/billing-data'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (res.ok) {
          const data = await res.json();
          setBillingData(data);
        }
        // Don't show error for billing data - it's optional
      } catch (err) {
        // Silently fail for billing data
      } finally {
        setBillingDataLoading(false);
      }
    };
    
    fetchBillingData();
  }, [token]);

  // Filter and select packages by category
  const filteredPackages = lessonPackages.filter(pkg => pkg.category === selectedCategory);
  
  // Check which categories have available packages
  const getAvailablePackagesForCategory = (category) => {
    return lessonPackages.filter(pkg => pkg.category === category);
  };
  
  const hasAvailablePackages = (category) => {
    return getAvailablePackagesForCategory(category).length > 0;
  };
  
  useEffect(() => {
    if (filteredPackages.length > 0) {
      setSelectedPackageId(filteredPackages[0].id);
    } else {
      setSelectedPackageId('');
    }
  }, [selectedCategory, lessonPackages]);

  // Auto-switch to an available category if current category has no packages
  useEffect(() => {
    if (lessonPackages.length > 0 && !hasAvailablePackages(selectedCategory)) {
      const availableCategory = sampleCategories.find(cat => hasAvailablePackages(cat.key));
      if (availableCategory) {
        setSelectedCategory(availableCategory.key);
      }
    }
  }, [lessonPackages, selectedCategory]);
  const selectedPackage = filteredPackages.find(pkg => pkg.id === selectedPackageId) || filteredPackages[0];

  const handlePurchase = async () => {
    if (!token) {
      setError('Bitte logge dich ein, um ein Paket zu kaufen.');
      return;
    }

    if (!selectedPackageId) {
      setError('Bitte wähle ein Paket aus.');
      return;
    }

    setPurchasing(true);
    setError(null);

    try {
      const response = await fetch(config.getApiUrl('/api/purchase'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: selectedPackageId
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Fehler beim Kauf des Pakets');
      }

      // Navigate to success page
      navigate('/purchase-success');
    } catch (err) {
      setError(err.message);
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <PageLayout>
      <Container>
        <Title>Stundenpaket kaufen</Title>
        {/* Show message if any billing field is empty */}
        {(!billingData.name || !billingData.street || !billingData.zipCode || !billingData.city) && !billingDataLoading && (
          <MessageStrip variant="warning" compact>
            <MessageIcon role="img" aria-label="Hinweis">⚠️</MessageIcon>
            <MessageText>
              Bitte vervollständige deine Daten, um Stundenpakete kaufen zu können.
            </MessageText>
          </MessageStrip>
        )}
        {loading ? (
          <div>Lade Stundenpakete...</div>
        ) : error ? (
          <div style={{ color: 'red' }}>{error}</div>
        ) : (
          <>
            <Section>
              {isMobile && <SectionTitle>Kategorie auswählen</SectionTitle>}
              {isMobile ? (
                <Dropdown
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                >
                  {sampleCategories.map(cat => {
                    const isDisabled = !hasAvailablePackages(cat.key);
                    return (
                      <option 
                        value={cat.key} 
                        key={cat.key}
                        disabled={isDisabled}
                      >
                        {cat.label}{isDisabled ? ' (Bald verfügbar)' : ''}
                      </option>
                    );
                  })}
                </Dropdown>
              ) : (
                <SegmentedButtonGroup>
                  {sampleCategories.map(cat => {
                    const isDisabled = !hasAvailablePackages(cat.key);
                    return (
                      <SegmentedButton
                        key={cat.key}
                        type="button"
                        active={selectedCategory === cat.key}
                        disabled={isDisabled}
                        onClick={() => !isDisabled && setSelectedCategory(cat.key)}
                      >
                        {cat.label}
                        {isDisabled && <ComingSoonText>(Bald verfügbar)</ComingSoonText>}
                      </SegmentedButton>
                    );
                  })}
                </SegmentedButtonGroup>
              )}
            </Section>
            <Section>
              <SectionTitle>Stundenpaket auswählen</SectionTitle>
              <Dropdown
                value={selectedPackageId}
                onChange={e => setSelectedPackageId(e.target.value)}
              >
                {filteredPackages.map(pkg => (
                  <option value={pkg.id} key={pkg.id}>{pkg.name}</option>
                ))}
              </Dropdown>
            </Section>
            <Section>
            <SectionTitle style={{marginBottom: -5}}>Stundenpaketbeschreibung</SectionTitle>
              <DescriptionBox category={selectedCategory}>
                {selectedPackage?.description || ''}
              </DescriptionBox>
            </Section>
            <ContractNotice>
              <PriceSection>
                Preis: {selectedPackage?.price ? `${selectedPackage.price}${selectedPackage.price.includes('€') ? '' : ' €'}` : ''}
              </PriceSection>
              {selectedPackage?.pricePerLesson && (
                <PricePerLessonSection>
                  Preis pro Stunde: {selectedPackage.pricePerLesson}
                </PricePerLessonSection>
              )}
              Alle Preise sind Endpreise. Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.<br></br><br></br>Mit dem Kauf erwirbst du ein Stundenbudget entsprechend dem gewählten Paket. 
              Dieses Budget kann für die Buchung von Unterrichtsstunden genutzt werden. Wird es nicht genutzt, verfällt es nach Ablauf von einem Jahr ab Kaufdatum ersatzlos. Die Terminvereinbarung erfolgt nach der Buchung individuell über die <i>Neue Stunde buchen</i> Funktionalität auf der <i>Mein Bereich</i> Seite. Termine sind nur in den verfügbaren Zeitfenstern möglich und können pro gebuchter Stunde bis zu 3 Mal verschoben werden. Eine Übersicht der aktuellen Verfügbarkeiten findest du{' '}
              <AGBLink 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowSlotsPopup(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                hier
              </AGBLink>.<br></br><br></br>Die Rechnung erhältst du direkt nach dem Kauf per E-Mail. Der Rechnungsbetrag ist innerhalb von 14 Tagen per Überweisung, SEPA-Lastschrift oder PayPal zu begleichen, unabhängig davon, wann die erste Unterrichtsstunde stattfindet oder ob alle Stunden genutzt werden.
              {selectedPackage?.additionalLegalInfo && (
                <>
                  <br></br><br></br>
                  {selectedPackage.additionalLegalInfo}
                </>
              )}
              <br></br><br></br>Bei Fragen erreichst du uns unter: <AGBLink href="mailto:mey@meyteaches.de">mey@meyteaches.de</AGBLink>
            </ContractNotice>

            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="widerruf"
                checked={widerrufChecked}
                onChange={e => setWiderrufChecked(e.target.checked)}
              />
              <label htmlFor="widerruf">
                Ich habe die <WiderrufLink href="/terms#widerruf" target="_blank" rel="noopener noreferrer">Widerrufsbelehrung</WiderrufLink> gelesen und akzeptiere sie.
              </label>
            </CheckboxContainer>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="agb"
                checked={agbChecked}
                onChange={e => setAgbChecked(e.target.checked)}
              />
              <label htmlFor="agb">
                Ich habe die{' '}
                <AGBLink href="/terms#agb" target="_blank" rel="noopener noreferrer">AGB</AGBLink> gelesen und akzeptiere sie.
              </label>
            </CheckboxContainer>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id="earlyExecution"
                checked={earlyExecutionChecked}
                onChange={e => setEarlyExecutionChecked(e.target.checked)}
              />
              <label htmlFor="earlyExecution">
                Ich stimme ausdrücklich zu, dass die Ausführung des Unterrichts vor Ablauf der Widerrufsfrist beginnen kann, wenn ich einen Termin innerhalb der 14-tägigen Widerrufsfrist buche, und mir ist bewusst, dass ich in diesem Fall für bereits erbrachte Unterrichtsstunden mein Widerrufsrecht verliere.
              </label>
            </CheckboxContainer>
                        {error && (
                          <div style={{ color: 'red', textAlign: 'center', marginBottom: '16px' }}>
                            {error}
                          </div>
                        )}
                        <ButtonContainer>
               <CancelButton onClick={() => navigate('/mypage')}>Abbrechen</CancelButton>
               <ActionButton 
                 disabled={!widerrufChecked || !agbChecked || !earlyExecutionChecked || purchasing || !billingData.name || !billingData.street || !billingData.zipCode || !billingData.city}
                 onClick={handlePurchase}
               >
                 {purchasing ? 'Kaufe...' : 'Zahlungspflichtig kaufen'}
               </ActionButton>
             </ButtonContainer>
          </>
        )}
      </Container>
      
      <LessonBookingPopup
        isOpen={showSlotsPopup}
        onClose={() => setShowSlotsPopup(false)}
        title="Verfügbare Termine anzeigen"
        availableBudgets={[]}
        onLessonBooked={() => {}} // No-op since this is just for viewing
        viewOnly={true}
      />
    </PageLayout>
  );
};

export default LessonPackagePurchasePage; 